import React, { useState, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";

const GlobalFilters = ({
  globalFilters,
  setGlobalFilters,
  updateGlobalFilter,
  variables,
  tabs = [],
  removeGlobalFilter,
}) => {
  const [showList, setShowList] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const handleSelectVariable = (key) => {
    const variable = variables[key];
    if (!variable) return;

    const label = variable.title || variable.Qtitle || key;
    const variableName = variable.varName?.[0]?.Name || variable.variableName || key;

    // --- BUILD OPTIONS EXACTLY LIKE YOUR JSON STRUCTURE ---
    const options =
      variable.values?.map((v, i) => ({
        Id: i + 1,
        Name: v.title || v.label || v.Name || v,
        ParentId: v.ParentId || [0],
        Selected: v.Selected ?? false,
        SortOrder: i + 1,
        Value: v.value || v.Value || v.title || v.Name || String(i + 1),
        Visible: v.Visible ?? false   // IMPORTANT: your expected JSON had Visible: false
      })) || [];


    // Find next numeric ID
    const nextId =
      globalFilters.length > 0
        ? Math.max(...globalFilters.map(f => Number(f.id))) + 1
        : 1;
    // --- FINAL GLOBAL FILTER OBJECT MATCHING YOUR JSON ---
    const newFilter = {
      id: nextId,
      global: 0,
      parent_id: null,
      label: label,
      variable_name: variableName,
      sort_order: globalFilters.length + 1,
      Selected: null,
      multiple: 0,
      visible: true,
      hide_on_tab: false,
      positioning: 0,
      RoleId: null,
      options: options,
      selectedValue: "", // For tracking the current selection
    };


    setGlobalFilters((prev) => [...prev, newFilter]);
    setShowList(false);
    setSearchTerm("");
  };


  // Collect all variable names used in Local Filters across tabs
  const localFilterVariables = tabs.flatMap((tab) =>
    tab.filters.map((f) => f.variable_name)
  );

  // Exclude both global + local variables
  const availableVariables = useMemo(
    () =>
      Object.keys(variables || {}).filter(
        (key) =>
          !globalFilters.some((gf) => gf.variable_name === key) &&
          !localFilterVariables.includes(key) &&
          (variables[key].title || key).toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [variables, globalFilters, localFilterVariables, searchTerm]
  );

  return (
    <div
      style={{
        background: "#f0f9ff",
        border: "1px solid #bae6fd",
        borderRadius: "8px",
        padding: "16px",
        // marginBottom: "16px",
        position: "relative",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h4 style={{ color: "#0369a1", margin: 0 }}>🌍 Global Filters</h4>

        <button
          onClick={() => setShowList((prev) => !prev)}
          style={{
            background: "#0369a1",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            padding: "6px 10px",
            cursor: "pointer",
          }}
        >
          + Add Global Filter
        </button>
      </div>

      {/* Dropdown List */}
      {showList && (
        <div
          style={{
            position: "absolute",
            top: "55px",
            right: "16px",
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: "8px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            zIndex: 100,
            maxHeight: "300px",
            overflowY: "auto",
            width: "260px",
            padding: "8px",
          }}
        >
          {/* Search Box */}
          <input
            type="text"
            placeholder="Search variable..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "6px 8px",
              marginBottom: "8px",
              borderRadius: 6,
              border: "1px solid #cbd5e1",
            }}
          />

          {availableVariables.length > 0 ? (
            availableVariables.map((key) => (
              <div
                key={key}
                onClick={() => handleSelectVariable(key)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid #f1f5f9",
                  fontSize: "14px",
                  color: "#334155",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f9ff")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                {variables[key].title || key}
              </div>
            ))
          ) : (
            <div style={{ padding: "10px", color: "#94a3b8" }}>
              All variables already added as filters.
            </div>
          )}
        </div>
      )}

      {/* Render Global Filters */}
      {globalFilters.length === 0 ? (
        <p style={{ color: "#64748b", marginTop: "12px" }}>No global filters added.</p>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "12px",
            marginTop: "12px",
          }}
        >
          {globalFilters.map((f) => (
            <div
              key={f.id}
              style={{
                position: "relative",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                padding: "10px",
              }}
            >
              {/* Label + Remove Button */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "6px",
                }}
              >
                <label style={{ fontSize: "14px", fontWeight: "500", color: "#334155" }}>
                  {f.label}
                </label>
                <button
                  onClick={() => removeGlobalFilter(f.id)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#ef4444",
                    fontSize: "16px",
                    cursor: "pointer",
                    lineHeight: "1",
                  }}
                  title="Remove Global Filter"
                >
                  ×
                </button>
              </div>

              {/* Dropdown */}
              <select
                value={f.selectedValue || ""} // use lowercase 'selectedValue'
                onChange={(e) => updateGlobalFilter(f.id, e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                <option value="">All</option>
                {f.options?.map((opt) => (
                  <option key={opt.Id} value={opt.Value}>
                    {opt.Name}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GlobalFilters;
